/**
 * Centralized Mock Data
 * 
 * All mock data for the application in one place for easy editing.
 * In a real application, this would be replaced with API calls.
 */

import type { User, Agent, Policy, AppNotification, Alert } from '../types';

// ============================================================================
// USER DATA
// ============================================================================

export const mockUser: User = {
  id: '1',
  firstName: 'Sarah',
  lastName: 'Shin',
  email: 'sarah.shin@wfg.com',
  role: 'agent',
  photoUrl: 'https://i.pravatar.cc/150?img=5',
  hierarchy: {
    level: 5,
    title: 'Executive Vice Chair',
    since: '2021'
  }
};

// ============================================================================
// NOTIFICATIONS & ALERTS
// ============================================================================

export const mockNotifications: AppNotification[] = [
  {
    id: '1',
    title: 'New Achievement Unlocked',
    message: "You've reached 500K milestone!",
    timestamp: '2 hours ago',
    type: 'success',
    unread: true,
  },
  {
    id: '2',
    title: 'Policy Renewal Due',
    message: '3 policies require attention this week',
    timestamp: '3 hours ago',
    type: 'warning',
    unread: true,
  },
  {
    id: '3',
    title: 'Team Update',
    message: 'Jennifer Davis joined your team',
    timestamp: 'Yesterday',
    type: 'info',
    unread: false,
  },
];

export const mockAlerts: Alert[] = [
  {
    id: '1',
    type: 'error',
    title: 'Action Required',
    message: 'License renewal deadline approaching - 5 days remaining',
    severity: 'critical',
    timestamp: '2024-02-19T10:00:00Z',
    dismissed: false,
  },
  {
    id: '2',
    type: 'warning',
    title: 'Low Activity',
    message: 'Team member Bob Lowel has not logged activity in 7 days',
    severity: 'warning',
    timestamp: '2024-02-19T09:00:00Z',
    dismissed: false,
  },
];

// ============================================================================
// TEAM / AGENT DATA
// ============================================================================

export const mockTeamMembers: Agent[] = [
  {
    id: '1',
    user: {
      id: '1',
      firstName: 'John',
      lastName: 'Smith',
      email: 'john.smith@example.com',
      photoUrl: 'https://i.pravatar.cc/150?img=1',
      role: 'agent',
      hierarchy: { level: 2 }
    },
    performanceTier: 'platinum',
    metrics: {
      totalPolicies: 145,
      activePolicies: 132,
      totalCommissions: 285000,
      monthlyCommissions: 12500,
      yearToDateCommissions: 145000
    }
  },
  {
    id: '2',
    user: {
      id: '2',
      firstName: 'Sarah',
      lastName: 'Johnson',
      email: 'sarah.j@example.com',
      photoUrl: 'https://i.pravatar.cc/150?img=2',
      role: 'agent',
      hierarchy: { level: 2 }
    },
    performanceTier: 'gold',
    metrics: {
      totalPolicies: 98,
      activePolicies: 89,
      totalCommissions: 195000,
      monthlyCommissions: 9800,
      yearToDateCommissions: 98000
    }
  },
  {
    id: '3',
    user: {
      id: '3',
      firstName: 'Michael',
      lastName: 'Chen',
      email: 'michael.c@example.com',
      photoUrl: 'https://i.pravatar.cc/150?img=3',
      role: 'agent',
      hierarchy: { level: 2 }
    },
    performanceTier: 'silver',
    metrics: {
      totalPolicies: 67,
      activePolicies: 61,
      totalCommissions: 125000,
      monthlyCommissions: 6200,
      yearToDateCommissions: 67000
    }
  },
  {
    id: '4',
    user: {
      id: '4',
      firstName: 'Jennifer',
      lastName: 'Davis',
      email: 'jennifer.d@example.com',
      photoUrl: 'https://i.pravatar.cc/150?img=4',
      role: 'agent',
      hierarchy: { level: 2 }
    },
    performanceTier: 'gold',
    metrics: {
      totalPolicies: 112,
      activePolicies: 105,
      totalCommissions: 220000,
      monthlyCommissions: 11000,
      yearToDateCommissions: 115000
    }
  }
];

// Agent detail profiles
export const mockAgentProfiles: Record<string, {
  id: string;
  user: User;
  performanceTier: 'bronze' | 'silver' | 'gold' | 'platinum';
  joinDate: string;
  licenseNumber: string;
  specializations: string[];
}> = {
  '1': {
    id: '1',
    user: {
      id: '1',
      firstName: 'John',
      lastName: 'Smith',
      email: 'john.smith@example.com',
      photoUrl: 'https://i.pravatar.cc/150?img=1',
      role: 'agent',
      hierarchy: { level: 2 }
    },
    performanceTier: 'platinum',
    joinDate: '2019-03-15',
    licenseNumber: 'CA-12345678',
    specializations: ['Life Insurance', 'Retirement Planning', 'Investment Services']
  }
};

// Agent performance metrics
export const mockAgentMetrics: Record<string, {
  totalPolicies: number;
  activePolicies: number;
  totalCommissions: number;
  monthlyCommissions: number;
  yearToDateCommissions: number;
  teamSize?: number;
  directReports?: number;
}> = {
  '1': {
    totalPolicies: 145,
    activePolicies: 132,
    totalCommissions: 285000,
    monthlyCommissions: 12500,
    yearToDateCommissions: 145000,
    teamSize: 8,
    directReports: 3
  }
};

// ============================================================================
// POLICY / BOOK OF BUSINESS DATA
// ============================================================================

export const mockPolicies: Policy[] = [
  {
    id: '1',
    policyNumber: 'POL-2024-001',
    type: 'Life Insurance',
    status: 'active',
    client: {
      id: 'c1',
      name: 'Robert Anderson',
      firstName: 'Robert',
      lastName: 'Anderson',
      email: 'robert.a@example.com',
      phone: '(555) 123-4567'
    },
    coverage: {
      amount: 500000,
      premium: 125,
      term: '20 years'
    },
    dates: {
      effectiveDate: '2024-01-15',
      expirationDate: '2034-01-15',
      renewalDate: '2025-01-15'
    },
    agent: {
      id: '1',
      name: 'Sarah Shin'
    },
    startDate: '2024-01-15',
    renewalDate: '2025-01-15',
    carrier: 'Transamerica'
  },
  {
    id: '2',
    policyNumber: 'POL-2024-002',
    type: 'Annuity',
    status: 'active',
    client: {
      id: 'c2',
      name: 'Maria Garcia',
      firstName: 'Maria',
      lastName: 'Garcia',
      email: 'maria.g@example.com',
      phone: '(555) 234-5678'
    },
    coverage: {
      amount: 250000,
      premium: 85,
      term: '10 years'
    },
    dates: {
      effectiveDate: '2023-06-20',
      expirationDate: '2033-06-20',
      renewalDate: '2024-06-20'
    },
    agent: {
      id: '1',
      name: 'Sarah Shin'
    },
    startDate: '2023-06-20',
    renewalDate: '2024-06-20',
    carrier: 'Nationwide'
  },
  {
    id: '3',
    policyNumber: 'POL-2023-045',
    type: 'Life Insurance',
    status: 'active',
    client: {
      id: 'c3',
      name: 'James Wilson',
      firstName: 'James',
      lastName: 'Wilson',
      email: 'james.w@example.com',
      phone: '(555) 345-6789'
    },
    coverage: {
      amount: 750000,
      premium: 195,
      term: '30 years'
    },
    dates: {
      effectiveDate: '2023-03-10',
      expirationDate: '2053-03-10',
      renewalDate: '2024-03-10'
    },
    agent: {
      id: '1',
      name: 'Sarah Shin'
    },
    startDate: '2023-03-10',
    renewalDate: '2024-03-10',
    carrier: 'AIG'
  },
  {
    id: '4',
    policyNumber: 'POL-2024-015',
    type: 'Disability Insurance',
    status: 'active',
    client: {
      id: 'c4',
      name: 'Linda Martinez',
      firstName: 'Linda',
      lastName: 'Martinez',
      email: 'linda.m@example.com',
      phone: '(555) 456-7890'
    },
    coverage: {
      amount: 100000,
      premium: 75,
      term: '5 years'
    },
    dates: {
      effectiveDate: '2024-02-01',
      expirationDate: '2029-02-01',
      renewalDate: '2025-02-01'
    },
    agent: {
      id: '1',
      name: 'Sarah Shin'
    },
    startDate: '2024-02-01',
    renewalDate: '2025-02-01',
    carrier: 'Guardian'
  },
  {
    id: '5',
    policyNumber: 'POL-2023-089',
    type: 'Life Insurance',
    status: 'pending',
    client: {
      id: 'c5',
      name: 'David Lee',
      firstName: 'David',
      lastName: 'Lee',
      email: 'david.l@example.com',
      phone: '(555) 567-8901'
    },
    coverage: {
      amount: 300000,
      premium: 95,
      term: '15 years'
    },
    dates: {
      effectiveDate: '2023-11-15',
      expirationDate: '2038-11-15',
      renewalDate: '2024-11-15'
    },
    agent: {
      id: '1',
      name: 'Sarah Shin'
    },
    startDate: '2023-11-15',
    renewalDate: '2024-11-15',
    carrier: 'MetLife'
  },
  {
    id: '6',
    policyNumber: 'POL-2024-022',
    type: 'Annuity',
    status: 'active',
    client: {
      id: 'c6',
      name: 'Patricia Brown',
      firstName: 'Patricia',
      lastName: 'Brown',
      email: 'patricia.b@example.com',
      phone: '(555) 678-9012'
    },
    coverage: {
      amount: 400000,
      premium: 110,
      term: '20 years'
    },
    dates: {
      effectiveDate: '2024-01-05',
      expirationDate: '2044-01-05',
      renewalDate: '2025-01-05'
    },
    agent: {
      id: '1',
      name: 'Sarah Shin'
    },
    startDate: '2024-01-05',
    renewalDate: '2025-01-05',
    carrier: 'Pacific Life'
  }
];

// ============================================================================
// COMMISSION DATA
// ============================================================================

export interface CommissionRecord {
  id: string;
  agentName: string;
  policyNumber: string;
  clientName: string;
  policyType: string;
  premium: number;
  commissionRate: number;
  commissionAmount: number;
  status: 'paid' | 'pending' | 'processing';
  paymentDate: string;
}

// Generate commission data
export function generateCommissionData(): CommissionRecord[] {
  const firstNames = ['Jennifer', 'Antonio', 'Philip', 'Bob', 'Julia', 'Michael', 'Sarah', 'David', 'Maria', 'James'];
  const lastNames = ['Davis', 'Rodriguez', 'Chen', 'Lowel', 'Anderson', 'Wilson', 'Johnson', 'Martinez', 'Garcia', 'Lee'];
  const policyTypes = ['Life Insurance', 'Annuity', 'Disability Insurance', 'Long-term Care'];
  const statuses: ('paid' | 'pending' | 'processing')[] = ['paid', 'paid', 'paid', 'paid', 'pending', 'processing'];

  const records: CommissionRecord[] = [];
  
  for (let i = 0; i < 50; i++) {
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    const premium = Math.floor(Math.random() * 500) + 50;
    const commissionRate = Math.floor(Math.random() * 30) + 10;
    const commissionAmount = Math.floor((premium * commissionRate) / 100);
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    
    const date = new Date();
    date.setDate(date.getDate() - Math.floor(Math.random() * 90));
    
    records.push({
      id: `comm-${i + 1}`,
      agentName: `${firstName} ${lastName}`,
      policyNumber: `POL-2024-${String(i + 1).padStart(3, '0')}`,
      clientName: `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastNames[Math.floor(Math.random() * lastNames.length)]}`,
      policyType: policyTypes[Math.floor(Math.random() * policyTypes.length)],
      premium,
      commissionRate,
      commissionAmount,
      status,
      paymentDate: date.toISOString().split('T')[0]
    });
  }
  
  return records.sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime());
}

export const mockCommissions = generateCommissionData();

// ============================================================================
// DASHBOARD METRICS
// ============================================================================

export const mockDashboardMetrics = {
  points: {
    current: 239423,
    label: 'Rolling 12 Net Points'
  },
  recruits: {
    current: 35,
    label: 'Rolling 12 Recruits'
  },
  cashFlow: {
    current: 48312,
    label: 'Rolling 12 Cash Flow'
  },
  commissions: {
    current: 1215,
    chartData: [45, 65, 55, 40, 35, 50, 70, 60, 55, 45, 50, 75]
  },
  book: {
    current: 8750,
    chartData: [60, 75, 65, 50, 45, 55, 80, 70, 65, 55, 60, 85]
  },
  pointRatio: {
    current: 80,
    chartData: [70, 75, 80, 65, 60, 68, 85, 78, 82, 75, 80, 88]
  },
  team: {
    totalAgents: 24,
    activeAgents: 22,
    totalPolicies: 310,
    quotaPercentage: 88
  }
};
